import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Star,
  Eye,
  Pencil,
  MoreHorizontal,
  Search,
  Upload,
  LayoutGrid,
  List,
  Frown,
  Plus,
  Sparkles,
  User,
  CheckCircle2,
  Pause
} from 'lucide-react';
import { smeService, getApiErrorMessage } from '../services/api';
import { getUser } from '../services/auth';
import { getCategoryDisplayName, getSubCategoryDisplayName, getTypeDisplayName } from '../constants/smeCategories';
import { CategoryIconMap, categoryColorMap } from './SMESidebar';
import { useBrandedDialog } from './BrandedDialog';
import './SMEList.css';

/* ── Badge colors per category ── */
export const getSubCategoryColor = (subStr) => {
  if (!subStr) return { bg: '#e2e8f0', color: '#475569' };
  const s = subStr.toLowerCase();
  if (s.includes('law')) return { bg: '#e0e7ff', color: '#4338ca' };
  if (s.includes('finance') || s.includes('bank') || s.includes('risk')) return { bg: '#dcfce7', color: '#15803d' };
  if (s.includes('marketing')) return { bg: '#fce7f3', color: '#be185d' };
  if (s.includes('tech') || s.includes('software') || s.includes('ai') || s.includes('cyber')) return { bg: '#f3e8ff', color: '#7e22ce' };
  if (s.includes('design')) return { bg: '#fef3c7', color: '#b45309' };
  if (s.includes('science')) return { bg: '#e0f2fe', color: '#0369a1' };
  if (s.includes('health') || s.includes('medical')) return { bg: '#ffe4e6', color: '#be123c' };
  if (s.includes('eco') || s.includes('business')) return { bg: '#ffedd5', color: '#c2410c' };
  return { bg: '#f1f5f9', color: '#475569' };
};

const ITEMS_PER_PAGE = 12;
const ORG_ADMIN_ERROR = 'You are not admin of this org';
const SME_TYPE_ALIASES = {
  science: 'Science',
  business: 'Business',
  technology: 'Technology',
  tech: 'Technology',
  healthcare: 'Healthcare',
  health: 'Healthcare',
  medical: 'Healthcare',
  politics: 'Politics',
  political: 'Politics',
  law: 'Law',
  legal: 'Law',
  education: 'Education',
  media: 'Media',
  engineering: 'Engineering',
  artsandhumanities: 'ArtsAndHumanities',
  arts: 'ArtsAndHumanities',
  humanities: 'ArtsAndHumanities',
  'arts&humanities': 'ArtsAndHumanities',
  environment: 'Environment',
  environmental: 'Environment',
  sportsandwellness: 'SportsAndWellness',
  sports: 'SportsAndWellness',
  wellness: 'SportsAndWellness',
  fitness: 'SportsAndWellness'
};

function normalizeSMEType(value) {
  if (!value) return '';
  const compact = String(value).trim().replace(/\s+/g, '').replace(/-/g, '').toLowerCase();
  return SME_TYPE_ALIASES[compact] || String(value).trim();
}

function formatAuditUser(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.userName || [value.firstName, value.lastName].filter(Boolean).join(' ') || value.name || value._id || '';
}

function parseCsvLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function parseSMEImportFile(fileName, text) {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');

  if (fileName.toLowerCase().endsWith('.json') || looksLikeJson) {
    try {
      const parsed = JSON.parse(trimmed);
      const rows = Array.isArray(parsed) ? parsed : parsed.smes || parsed.data || parsed.documents || [];
      return rows.map(normalizeImportedSMERow);
    } catch (error) {
      try {
        const rows = trimmed
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(Boolean)
          .map(line => JSON.parse(line));
        return rows.map(normalizeImportedSMERow);
      } catch {
        if (fileName.toLowerCase().endsWith('.json')) throw error;
      }
    }
  }

  const lines = trimmed.split(/\r?\n/).filter(Boolean);
  const headers = parseCsvLine(lines[0]).map(header => header.trim());
  return lines.slice(1).map(line => {
    const values = parseCsvLine(line);
    return headers.reduce((row, header, index) => {
      const value = values[index];
      if (value !== undefined && value !== '') {
        row[header] = value;
      }
      return row;
    }, {});
  }).map(normalizeImportedSMERow);
}

function normalizeImportedSMERow(row = {}) {
  const pick = (...keys) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
    }
    return '';
  };

  return {
    name: pick('name', 'Name', 'smeName', 'SME Name'),
    type: normalizeSMEType(pick('type', 'Type', 'domain', 'Domain')),
    smeCategory: pick('smeCategory', 'SME Category', 'category', 'Category', 'sub-domain', 'Sub-Domain'),
    subCategory: pick('subCategory', 'Sub Category', 'specialisation', 'specialization', 'Specialisation', 'Specialization'),
    prompt: pick('prompt', 'Prompt', 'AI Prompt', 'aiPrompt', 'characterPrompt'),
    description: pick('description', 'Description'),
    useWebSearch: pick('useWebSearch', 'Use Web Search'),
    isFavorite: pick('isFavorite', 'favorite', 'favourite', 'Favorite', 'Favourite')
  };
}

/* ════════════════════════════════════════════════════════════ */
const SMEList = () => {
  const [smes, setSmes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsMode, setSettingsMode] = useState(false);
  const [selectedSmeIds, setSelectedSmeIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pastedImportText, setPastedImportText] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [permissionDialog, setPermissionDialog] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { alert, confirm, toast } = useBrandedDialog();
  const importInputRef = useRef(null);
  const user = getUser();
  const canManageResourcePrompts = Boolean(user?.permissions?.canManageResourcePrompts);

  useEffect(() => { fetchSMEs(); }, []);

  useEffect(() => {
    const queryFilter = new URLSearchParams(location.search).get('filter') || 'all';
    setActiveFilter(queryFilter);
    setCurrentPage(1);
  }, [location.search]);

  useEffect(() => {
    const handleOutsideClick = () => setOpenDropdownId(null);
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const fetchSMEs = async () => {
    try {
      setLoading(true);
      const response = await smeService.getAll();
      const rows = response.data || [];
      setSmes(rows);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load SMEs.'));
      console.error('Error fetching SMEs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSettingsMode = () => {
    setSettingsMode(prev => !prev);
    setSelectedSmeIds([]);
  };

  const toggleSelectedSme = (id) => {
    setSelectedSmeIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const selectAllSmes = () => setSelectedSmeIds(filteredSmes.map(s => s._id));

  const handleDeleteSelected = async () => {
    if (selectedSmeIds.length === 0) return;
    const confirmed = await confirm({
      title: 'Delete selected SMEs?',
      message: `Delete ${selectedSmeIds.length} selected SME${selectedSmeIds.length === 1 ? '' : 's'}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      setDeleting(true);
      await Promise.all(selectedSmeIds.map(id => smeService.delete(id)));
      setSmes(prev => prev.filter(s => !selectedSmeIds.includes(s._id)));
      setSelectedSmeIds([]);
      setSettingsMode(false);
    } catch (err) {
      showMutationError(err, 'Failed to delete selected SMEs');
      console.error('Error deleting selected SMEs:', err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleFavorite = async (id) => {
    const current = smes.find(sme => sme._id === id);
    const nextIsFavorite = !current?.isFavorite;
    const previousSmes = smes;
    setSmes(prev => prev.map(sme => (
      sme._id === id ? { ...sme, isFavorite: nextIsFavorite } : sme
    )));

    try {
      const response = await smeService.updateFavorite(id, nextIsFavorite);
      setSmes(prev => prev.map(sme => (
        sme._id === id ? response.data : sme
      )));
    } catch (err) {
      setSmes(previousSmes);
      alert({ title: 'Failed to update favorite', message: err.response?.data?.error || err.message || 'Please try again.', tone: 'danger' });
      console.error('Error updating favorite:', err);
    }
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const showMutationError = (err, fallback) => {
    const message = err.response?.data?.error || err.message || fallback;
    if (err.response?.status === 403 || message.toLowerCase().includes('admin')) {
      setPermissionDialog(ORG_ADMIN_ERROR);
      return;
    }
    alert({ title: fallback, message, tone: 'danger' });
  };

  const chooseImportFile = () => {
    setImportDialogOpen(false);
    importInputRef.current?.click();
  };

  const importOneByOne = async (rows) => {
    const created = [];
    const failed = [];

    for (const [index, row] of rows.entries()) {
      try {
        const response = await smeService.create(row);
        created.push(response.data);
      } catch (error) {
        failed.push({
          index,
          name: row.name || '',
          error: error.response?.data?.error || error.message
        });
      }
    }

    return { created, failed, failedCount: failed.length };
  };

  const importRows = async (rows, importSource) => {
    let result;
    try {
      const response = await smeService.import({
        smes: rows,
        importSource
      });
      result = response.data;
    } catch (error) {
      if (error.response?.status !== 404) throw error;
      result = await importOneByOne(rows);
    }

    const created = result?.created || [];
    const failedCount = result?.failedCount || 0;
    if (created.length) {
      setSmes(prev => [...created, ...prev]);
    }
    toast({
      title: 'Import complete',
      message: `Imported ${created.length} SME${created.length === 1 ? '' : 's'}${failedCount ? `; ${failedCount} failed validation.` : '.'}`,
      tone: failedCount ? 'default' : 'success',
    });
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const importedRows = parseSMEImportFile(file.name, text);
      if (!Array.isArray(importedRows) || importedRows.length === 0) {
        alert({ title: 'No records found', message: 'No SME records found in the selected file.' });
        return;
      }

      await importRows(importedRows, file.name);
    } catch (err) {
      const failed = err.response?.data?.failed;
      const firstFailure = Array.isArray(failed) && failed[0]
        ? ` Row ${failed[0].index + 1}: ${failed[0].error}`
        : '';
      alert({ title: 'Failed to import SMEs', message: `${err.response?.data?.error || err.message || 'Failed to import SMEs'}${firstFailure}`, tone: 'danger' });
      console.error('Error importing SMEs:', err);
    } finally {
      setImporting(false);
    }
  };

  const handlePasteImport = async () => {
    try {
      setImporting(true);
      const importedRows = parseSMEImportFile('pasted.json', pastedImportText);
      if (!Array.isArray(importedRows) || importedRows.length === 0) {
        alert({ title: 'No records found', message: 'No SME records found in the pasted content.' });
        return;
      }

      await importRows(importedRows, 'pasted-import');
      setPastedImportText('');
      setImportDialogOpen(false);
    } catch (err) {
      const failed = err.response?.data?.failed;
      const firstFailure = Array.isArray(failed) && failed[0]
        ? ` Row ${failed[0].index + 1}: ${failed[0].error}`
        : '';
      alert({ title: 'Failed to import pasted SMEs', message: `${err.response?.data?.error || err.message || 'Failed to import pasted SMEs'}${firstFailure}`, tone: 'danger' });
      console.error('Error importing pasted SMEs:', err);
    } finally {
      setImporting(false);
    }
  };

  const toggleSmeStatus = async (e, sme) => {
    e.stopPropagation();
    try {
      const newStatus = !sme.isActive;
      await smeService.toggleStatus(sme._id, newStatus);
      setSmes(prev => prev.map(s => s._id === sme._id ? { ...s, isActive: newStatus } : s));
    } catch (err) {
      console.error('Error toggling SME status:', err);
      showMutationError(err, 'Failed to update SME status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  /* ── Stats Counts ── */
  const totalCount = smes.length;
  const activeCount = smes.filter(s => s.isActive !== false).length;
  const disabledCount = smes.filter(s => s.isActive === false).length;

  /* ── Filtering + Sorting ── */
  const filteredSmes = smes
    .filter(sme => {
      if (statusFilter === 'active') return sme.isActive !== false;
      if (statusFilter === 'disabled') return sme.isActive === false;
      return true;
    })
    .filter(sme => {
      if (activeFilter === 'favorites') return Boolean(sme.isFavorite);
      if (activeFilter === 'my') return Boolean(sme.isMine);
      if (activeFilter === 'recent') return Boolean(sme.lastUsedAt);
      if (activeFilter !== 'all') return sme.type === activeFilter;
      return true;
    })
    .filter(sme => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        sme.name?.toLowerCase().includes(q) ||
        sme.type?.toLowerCase().includes(q) ||
        sme.smeCategory?.toLowerCase().includes(q) ||
        sme.subCategory?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (activeFilter === 'recent') return new Date(b.lastUsedAt || 0) - new Date(a.lastUsedAt || 0);
      if (sortBy === 'recent') return new Date(b.createdDate) - new Date(a.createdDate);
      if (sortBy === 'oldest') return new Date(a.createdDate) - new Date(b.createdDate);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });

  /* ── Pagination ── */
  const totalPages = Math.ceil(filteredSmes.length / ITEMS_PER_PAGE);
  const paginatedSmes = filteredSmes.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
    navigate(filter && filter !== 'all' ? `/?filter=${encodeURIComponent(filter)}` : '/');
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="smelist-page">
        <div className="smelist-main">
          <div className="app-loading-container">
            <div className="app-loading-spinner" />
            <p>Loading SMEs...</p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="smelist-page">
        <div className="smelist-main">
          <div className="smelist-error">{error}</div>
        </div>
      </div>
    );
  }

  /* ── Main render ── */
  return (
    <div className="smelist-page">
      {/* Main Content */}
      <div className="smelist-main">
        {permissionDialog && (
          <div className="smelist-modal-backdrop" role="presentation" onMouseDown={() => setPermissionDialog('')}>
            <div
              className="smelist-permission-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sme-permission-dialog-title"
              onMouseDown={event => event.stopPropagation()}
            >
              <h2 id="sme-permission-dialog-title">Permission required</h2>
              <p>{permissionDialog}</p>
              <button className="smelist-btn-generate" onClick={() => setPermissionDialog('')}>
                OK
              </button>
            </div>
          </div>
        )}
        {importDialogOpen && (
          <div className="smelist-modal-backdrop" role="presentation" onMouseDown={() => setImportDialogOpen(false)}>
            <div
              className="smelist-import-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sme-import-dialog-title"
              onMouseDown={event => event.stopPropagation()}
            >
              <div className="smelist-import-dialog-header">
                <div>
                  <h2 id="sme-import-dialog-title">Import SME</h2>
                  <p>Paste content from ChatGPT or upload a JSON/CSV file.</p>
                </div>
                <button
                  className="smelist-dialog-close"
                  onClick={() => setImportDialogOpen(false)}
                  aria-label="Close import dialog"
                >
                  x
                </button>
              </div>

              <div className="smelist-import-format">
                <div>
                  <h3>Ask ChatGPT for this structure</h3>
                  <p>Each SME should include <code>name</code>, <code>type</code>, <code>smeCategory</code>, <code>subCategory</code>, and <code>prompt</code>. The category fields help the SME appear in the right filters.</p>
                </div>
                <div>
                  <h3>Copy-ready JSON example</h3>
                  <pre>{'[\n  {\n    "name": "Daniel Rossi",\n    "type": "SportsAndWellness",\n    "smeCategory": "SportsScience",\n    "subCategory": "Coaching",\n    "description": "Football analyst focused on tactics, transfers, and match analysis.",\n    "prompt": "You are Daniel Rossi, a football analyst and subject matter expert. Explain tactics, formations, player performance, transfers, coaching strategies, club management, football history, statistics, and match predictions clearly.",\n    "useWebSearch": true\n  }\n]'}</pre>
                  <p>Use one of these broad domains for <code>type</code>: Science, Business, Technology, Healthcare, Politics, Law, Education, Media, Engineering, ArtsAndHumanities, Environment, SportsAndWellness.</p>
                </div>
                <div>
                  <h3>CSV headers</h3>
                  <pre>name,type,prompt,smeCategory,subCategory,description,useWebSearch,isFavorite</pre>
                  <p>For CSV, keep the first row exactly as the header. Put one SME on each row below it.</p>
                </div>
                <div>
                  <h3>Paste import data</h3>
                  <textarea
                    className="smelist-import-paste"
                    value={pastedImportText}
                    onChange={event => setPastedImportText(event.target.value)}
                    placeholder={'Paste JSON or CSV here.\n\nTip: Ask ChatGPT: "Create SMEs as a JSON array with name, type, smeCategory, subCategory, description, prompt, and useWebSearch."'}
                    rows="7"
                  />
                </div>
              </div>

              <div className="smelist-dialog-actions">
                <button className="smelist-btn-sm-outline" onClick={() => setImportDialogOpen(false)}>
                  Cancel
                </button>
                <button
                  className="smelist-btn-import"
                  onClick={handlePasteImport}
                  disabled={importing || !pastedImportText.trim()}
                >
                  Paste Import
                </button>
                <button className="smelist-btn-generate" onClick={chooseImportFile}>
                  <Upload size={15} strokeWidth={2.5} />
                  Choose File
                </button>
              </div>
            </div>
          </div>
        )}


        {/* ── Sticky Top Area ── */}
        <div className="smelist-sticky-top">
          {/* ── Page Header ── */}
          <div className="smelist-header">
          <div className="smelist-title-area">
            <h1 className="smelist-title">AI Subject Matter Experts</h1>
            <p className="smelist-subtitle">
              Manage your AI agents that specialize in specific domains and create expert content.
            </p>
          </div>
          <div className="smelist-header-actions">
            {canManageResourcePrompts && (
              <button className="smelist-btn-ghost" onClick={() => navigate('/prompts')}>
                Prompt Manager
              </button>
            )}
            <input
              ref={importInputRef}
              type="file"
              accept=".json,.csv,application/json,text/csv"
              className="smelist-import-input"
              onChange={handleImportFile}
            />
            <button className="smelist-btn-import" onClick={handleImportClick} disabled={importing}>
              <Upload size={15} strokeWidth={2} />
              {importing ? 'Importing...' : 'Import SME'}
            </button>
            
            <button className="smelist-btn-generate" onClick={() => navigate('/generate-article')}>
              <Sparkles size={15} strokeWidth={2.5} />
              Generate Article
            </button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="smelist-toolbar">
          <div className="smelist-search-wrap">
            <Search className="smelist-search-icon" size={16} strokeWidth={2} />
            <input
              className="smelist-search"
              type="text"
              placeholder="Search SMEs by name, specialization, or category..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>

          <div className="smelist-toolbar-right">
            <select
              className="smelist-select"
              value={
                activeFilter === 'all' || activeFilter === 'my' ||
                activeFilter === 'favorites' || activeFilter === 'recent'
                  ? 'all' : activeFilter
              }
              onChange={e => handleFilterChange(e.target.value)}
            >
              <option value="all">All Categories</option>
              {[...new Set(smes.map(s => s.type).filter(Boolean))].map(t => (
                <option key={t} value={t}>{getTypeDisplayName(t)}</option>
              ))}
            </select>

            <select className="smelist-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="recent">Most Recent</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name A–Z</option>
            </select>

            <div className="smelist-view-toggle">
              <button
                className={`smelist-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <LayoutGrid size={16} strokeWidth={2} />
              </button>
              <button
                className={`smelist-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List size={16} strokeWidth={2} />
              </button>
            </div>

            {smes.length > 0 && (
              <button
                className={`smelist-delete-toggle ${settingsMode ? 'active' : ''}`}
                onClick={toggleSettingsMode}
              >
                {settingsMode ? 'Done' : 'Delete'}
              </button>
            )}
          </div>
        </div>
        </div> {/* End Sticky Top Area */}

        {/* ── Stats Row ── */}
        <div className="smelist-stats-row">
          <div 
            className={`smelist-stat-card ${statusFilter === 'all' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'all' ? '1px solid #9333ea' : '1px solid #e5ebe8', boxShadow: statusFilter === 'all' ? '0 0 0 2px rgba(147, 51, 234, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#f3e8ff', color: '#9333ea' }}>
              <User size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total SMEs</p>
              <p className="stat-value">{totalCount}</p>
            </div>
          </div>
          <div 
            className={`smelist-stat-card ${statusFilter === 'active' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('active'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'active' ? '1px solid #16a34a' : '1px solid #e5ebe8', boxShadow: statusFilter === 'active' ? '0 0 0 2px rgba(22, 163, 74, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#dcfce7', color: '#16a34a' }}>
              <CheckCircle2 size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Active SMEs</p>
              <p className="stat-value">{activeCount}</p>
            </div>
          </div>
          <div 
            className={`smelist-stat-card ${statusFilter === 'disabled' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('disabled'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'disabled' ? '1px solid #ea580c' : '1px solid #e5ebe8', boxShadow: statusFilter === 'disabled' ? '0 0 0 2px rgba(234, 88, 12, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#ffedd5', color: '#ea580c' }}>
              <Pause size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Disabled SMEs</p>
              <p className="stat-value">{disabledCount}</p>
            </div>
          </div>
        </div>

        {/* ── Settings / Bulk Delete Bar ── */}
        {settingsMode && (
          <div className="smelist-settings-bar">
            <span>{selectedSmeIds.length} selected</span>
            <div className="smelist-settings-actions">
              <button className="smelist-btn-sm-outline" onClick={selectAllSmes} disabled={deleting}>
                Select All
              </button>
              <button className="smelist-btn-sm-outline" onClick={() => setSelectedSmeIds([])} disabled={deleting}>
                Clear
              </button>
              <button
                className="smelist-btn-sm-danger"
                onClick={handleDeleteSelected}
                disabled={selectedSmeIds.length === 0 || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Selected'}
              </button>
            </div>
          </div>
        )}

        {/* ── Empty State ── */}
        {filteredSmes.length === 0 ? (
          <div className="smelist-empty">
            <div className="smelist-empty-icon">
              <Frown size={48} strokeWidth={1.5} color="#9ca3af" />
            </div>
            <p className="smelist-empty-title">No SMEs found</p>
            <p className="smelist-empty-desc">
              {searchQuery ? 'Try adjusting your search.' : 'Create your first SME to get started!'}
            </p>
            <button className="smelist-btn-generate" onClick={() => navigate('/sme/new')}>
              <Plus size={15} strokeWidth={2.5} />
              Create New SME
            </button>
          </div>
        ) : (
          <>
            {/* ── Grid / List ── */}
            <div className={viewMode === 'grid' ? 'smelist-grid' : 'smelist-list'}>
              {paginatedSmes.map(sme => {
                const typeKey = sme.type || 'Default';
                const IconComponent = CategoryIconMap[typeKey] || CategoryIconMap.Default;
                const cMap = categoryColorMap[typeKey] || categoryColorMap.Default;
                const typeBadge = { bg: cMap.bg, color: cMap.color };
                const catColor  = getSubCategoryColor(sme.smeCategory);
                const isFav     = Boolean(sme.isFavorite);
                const isSelected = selectedSmeIds.includes(sme._id);

                const renderBadges = () => (
                  <div className="sme-card-badges">
                    <span
                      className="sme-badge"
                      style={{ background: typeBadge.bg, color: typeBadge.color }}
                    >
                      {getTypeDisplayName(sme.type)}
                    </span>
                    {sme.smeCategory && (
                      <span
                        className="sme-badge"
                        style={{ background: catColor.bg, color: catColor.color }}
                      >
                        {getCategoryDisplayName(sme.smeCategory)}
                      </span>
                    )}
                  </div>
                );

                return (
                  <div
                    key={sme._id}
                    className={`sme-card ${isSelected ? 'sme-card-selected' : ''} ${sme.isActive === false ? 'sme-card-disabled' : ''}`}
                    onClick={settingsMode ? () => toggleSelectedSme(sme._id) : undefined}
                    style={{ position: 'relative', zIndex: openDropdownId === sme._id ? 10 : 1 }}
                  >
                    {/* ── Card Header ── */}
                    <div className="sme-card-header">
                      <div className="sme-card-icon-name">
                        {settingsMode ? (
                          <label className="sme-checkbox-label" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectedSme(sme._id)}
                            />
                          </label>
                        ) : (
                          <div 
                            className="sme-card-cat-icon"
                            style={{ background: cMap.bg, color: cMap.color }}
                          >
                            <IconComponent size={24} strokeWidth={2.2} />
                          </div>
                        )}
                        <div className="sme-card-name-wrapper">
                          <h3 className="sme-card-name">{sme.name}</h3>
                          {renderBadges()}
                        </div>
                      </div>
                      
                      <button
                        className={`sme-fav-btn ${isFav ? 'active' : ''}`}
                        onClick={e => { e.stopPropagation(); toggleFavorite(sme._id); }}
                        title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star
                          size={16}
                          strokeWidth={2}
                          fill={isFav ? 'currentColor' : 'none'}
                        />
                      </button>
                    </div>

                    {/* ── Card Body ── */}
                    <div className="sme-card-body">
                      <p className="sme-card-desc">
                        {sme.subCategory
                          ? `Specializes in ${getSubCategoryDisplayName(sme.subCategory).toLowerCase()}.`
                          : sme.description || 'An AI subject matter expert.'}
                      </p>
                    </div>

                    {/* ── Card Footer ── */}
                    <div className="sme-card-footer">
                      <div className="sme-card-meta">
                        <span className="sme-status-dot">
                          <span className="sme-status-indicator" style={{ background: sme.isActive !== false ? '#22c55e' : '#94a3b8' }} />
                          {sme.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                        <span style={{ margin: '0 0.5rem', color: '#cbd5e1' }}>•</span>
                        <span>Created {formatDate(sme.createdDate)}</span>
                        {(formatAuditUser(sme.createdBy) || formatAuditUser(sme.updatedBy)) && (
                          <span className="sme-card-audit-line">
                            {formatAuditUser(sme.createdBy) && <span>Created by {formatAuditUser(sme.createdBy)}</span>}
                            {formatAuditUser(sme.updatedBy) && <span>Updated by {formatAuditUser(sme.updatedBy)}</span>}
                          </span>
                        )}
                      </div>

                      <div className="sme-card-actions">
                        <button
                          className="sme-action-btn"
                          onClick={e => { e.stopPropagation(); navigate(`/sme/${sme._id}`); }}
                        >
                          <Eye size={14} strokeWidth={2} />
                          View
                        </button>
                        <button
                          className="sme-action-btn"
                          onClick={e => { e.stopPropagation(); navigate(`/sme/${sme._id}/edit`); }}
                          disabled={settingsMode}
                        >
                          <Pencil size={14} strokeWidth={2} />
                          Edit
                        </button>
                        <div className="sme-action-more-wrapper" style={{ position: 'relative' }}>
                          <button
                            className="sme-action-btn sme-action-more"
                            onClick={e => { e.stopPropagation(); setOpenDropdownId(openDropdownId === sme._id ? null : sme._id); }}
                            title="More options"
                          >
                            <MoreHorizontal size={14} strokeWidth={2} />
                          </button>
                          {openDropdownId === sme._id && (
                            <div className="sme-dropdown-menu" style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '120px', padding: '4px 0' }}>
                              <button
                                style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#334155' }}
                                onClick={(e) => {
                                  toggleSmeStatus(e, sme);
                                  setOpenDropdownId(null);
                                }}
                              >
                                {sme.isActive !== false ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Footer: count + pagination ── */}
            <div className="smelist-footer">
              <span className="smelist-count">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredSmes.length)} of {filteredSmes.length} SMEs
              </span>
              {totalPages > 1 && (
                <div className="smelist-pagination">
                  <button
                    className="smelist-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`smelist-page-btn ${page === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="smelist-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SMEList;
